import { useCallback, useEffect, useRef } from "react";

export interface WorkoutSession {
  routineId: string;
  startedAt: string;
  lastUpdatedAt: string;
  activeExerciseId: string;
  currentSetIndex: number;
  completedSets: { [exerciseId: string]: boolean[] };
}

const SESSION_KEY_PREFIX = "clubbers_workout_session_";
const SESSION_EXPIRY_HOURS = 24;

const getSessionKey = (routineId: string) => `${SESSION_KEY_PREFIX}${routineId}`;

const isSessionExpired = (session: WorkoutSession): boolean => {
  const lastUpdated = new Date(session.lastUpdatedAt);
  const now = new Date();
  const hoursDiff = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60);
  return hoursDiff > SESSION_EXPIRY_HOURS;
};

export const useWorkoutSessionPersistence = (routineId: string | undefined) => {
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Get existing session for this routine
  const getSession = useCallback((): WorkoutSession | null => {
    if (!routineId) return null;
    
    try {
      const stored = localStorage.getItem(getSessionKey(routineId));
      if (!stored) return null;
      
      const session: WorkoutSession = JSON.parse(stored);
      
      // Check if session is expired
      if (isSessionExpired(session)) {
        localStorage.removeItem(getSessionKey(routineId));
        console.log('🗑️ Expired workout session removed for routine:', routineId);
        return null;
      }
      
      return session;
    } catch (error) {
      console.error('Error reading workout session:', error);
      return null;
    }
  }, [routineId]);

  // Save session with debounce
  const saveSession = useCallback((sessionData: Omit<WorkoutSession, 'routineId' | 'lastUpdatedAt'>) => {
    if (!routineId) return;

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the save to avoid too many writes
    debounceRef.current = setTimeout(() => {
      try {
        const session: WorkoutSession = {
          ...sessionData,
          routineId,
          lastUpdatedAt: new Date().toISOString(),
        };
        
        localStorage.setItem(getSessionKey(routineId), JSON.stringify(session));
        console.log('💾 Workout session saved:', {
          routineId,
          activeExerciseId: session.activeExerciseId,
          currentSetIndex: session.currentSetIndex,
          completedSetsCount: Object.values(session.completedSets).flat().filter(Boolean).length
        });
      } catch (error) {
        console.error('Error saving workout session:', error);
      }
    }, 300);
  }, [routineId]);

  // Clear session
  const clearSession = useCallback(() => {
    if (!routineId) return;
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    localStorage.removeItem(getSessionKey(routineId));
    console.log('🗑️ Workout session cleared for routine:', routineId);
  }, [routineId]);

  // Check if there's an active session
  const hasActiveSession = useCallback((): boolean => {
    const session = getSession();
    return session !== null;
  }, [getSession]);

  // Get time since session started
  const getSessionAge = useCallback((): string | null => {
    const session = getSession();
    if (!session) return null;
    
    const startedAt = new Date(session.startedAt);
    const now = new Date();
    const diffMs = now.getTime() - startedAt.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffHours > 0) {
      return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    } else if (diffMins > 0) {
      return `hace ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
    } else {
      return 'hace unos segundos';
    }
  }, [getSession]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    getSession,
    saveSession,
    clearSession,
    hasActiveSession,
    getSessionAge,
  };
};
